import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { ChevronDown, ClipboardCheck, TrendingUp } from 'lucide-react'
import { format } from 'date-fns'
import { decodeHtmlEntities } from '../../utils/displayText'
import TrendChartCard from './TrendChartCard'
import {
  buildDailyOverallScoreTrendSeries,
  buildPerMeetingTrendSeries,
  interruptionScore,
  overallMeetingScore,
  outputVelocityScore,
} from '../../utils/analyticsTrendScores'

/**
 * Meeting scorecard + trend chart. Uses the parent Analytics page period via `periodDashboard`
 * (same GET /api/analytics/dashboard payload as Quick Analytics) plus a separate trend payload for
 * scorecard trend and most-recent-meeting detail.
 */
export default function MeetingScorecardsSection({
  periodDashboard,
  loading,
  analyticsLabel,
  detailPeriodLabel,
  chartRange,
  /** Overrides the subtitle under \"Meeting Scorecard\" (analytics default references Quick Analytics). */
  subtitle,
  /** `daily` — one point per calendar day (average). `per_meeting` — one point per occurrence; enables navigation when `onScoreTrendMeetingNavigate` is set. */
  scoreTrendGranularity = 'daily',
  /** `/meeting/:meetingId` navigation for recurring scorecard trend dots (requires `scoreTrendGranularity="per_meeting"`). */
  onScoreTrendMeetingNavigate,
  /** Overrides Scorecard Trend chart description under the title. */
  trendChartDescription,
}) {
  const [expandedModels, setExpandedModels] = useState([])

  const scorecardModels = [
    { id: 'talk-equity', name: 'Talk Equity', description: 'Balanced participation across voices.' },
    { id: 'agenda-adherence', name: 'Agenda Adherence', description: 'Stayed aligned to planned topics.' },
    { id: 'interruptions', name: 'Conversation Flow', description: 'How smoothly the conversation flowed, with balanced turn-taking and minimal cross-talk.' },
    { id: 'decision-velocity', name: 'Output Velocity', description: 'Pace of producing confirmed outputs.' },
    { id: 'time-discipline', name: 'Time Discipline', description: 'Started and finished on time.' },
  ]

  const lastMeeting = useMemo(() => {
    const meetings = periodDashboard?.recentMeetingAnalytics ?? []
    const qualified = meetings.find((m) => {
      const mins = Number(m.totalMeetingMinutes ?? 0)
      return Number.isFinite(mins) && mins > 5
    })
    return qualified ?? meetings[0] ?? null
  }, [periodDashboard])

  const scores = useMemo(() => {
    const dashboardData = periodDashboard
    if (!dashboardData) return null
    if (dashboardData?.scorecardScores) return dashboardData.scorecardScores
    if (dashboardData?.quick) {
      const q = dashboardData.quick
      const mc = dashboardData.meetingCount || 1
      return {
        'talk-equity': Math.round((q.talkEquityAvg ?? 0) * 100),
        'agenda-adherence': Math.round((q.agendaAdherenceAvg ?? 0) * 100),
        'interruptions': interruptionScore(Math.round((q.interruptions ?? 0) / mc)),
        'decision-velocity': 0,
        'time-discipline':
          dashboardData.scorecardScores?.['time-discipline'] != null &&
          Number.isFinite(dashboardData.scorecardScores['time-discipline'])
            ? dashboardData.scorecardScores['time-discipline']
            : typeof dashboardData.efficiency?.timeDisciplineScore === 'number' &&
                Number.isFinite(dashboardData.efficiency.timeDisciplineScore)
              ? dashboardData.efficiency.timeDisciplineScore
              : null,
      }
    }
    return null
  }, [periodDashboard])

  const agendaAdherenceBreakdown = useMemo(() => {
    const meetings = periodDashboard?.recentMeetingAnalytics ?? []
    if (meetings.length === 0) return null
    let total = 0
    let completed = 0
    let tangentsDetected = 0
    for (const m of meetings) {
      total += m.agendaItemCount ?? 0
      completed += m.agendaItemCompleted ?? 0
      tangentsDetected += Number(m.tangentsDetectedCount ?? 0)
    }
    const outstanding = Math.max(0, total - completed)
    if (total === 0) return null
    return {
      total,
      completed,
      outstanding,
      tangentsDetected,
      label: meetings.length === 1 ? 'Selected period' : `Across ${meetings.length} meetings in period`,
    }
  }, [periodDashboard?.recentMeetingAnalytics])

  const outputVelocityBreakdown = useMemo(() => {
    const meetings = periodDashboard?.recentMeetingAnalytics ?? []
    if (meetings.length === 0) return null
    let decisionCount = 0
    let actionItemCount = 0
    let followUpCount = 0
    for (const m of meetings) {
      decisionCount += Number(m.decisionCount ?? 0)
      actionItemCount += Number(m.actionItemCount ?? 0)
      followUpCount += Number(m.followUpCount ?? 0)
    }
    return {
      decisionCount,
      actionItemCount,
      followUpCount,
      label: meetings.length === 1 ? 'Selected period' : `Across ${meetings.length} meetings in period`,
    }
  }, [periodDashboard?.recentMeetingAnalytics])

  const participantBreakdowns = useMemo(() => {
    const agg = periodDashboard?.aggregatedParticipants ?? {}
    const lastStats = lastMeeting?.participantStats ?? {}
    const displayNameFor = (id) => agg[id]?.displayName ?? lastStats[id]?.displayName ?? id
    const isBot = (id) => {
      const name = String(displayNameFor(id)).toLowerCase()
      return /rithmo|recall|zoom bot|fireflies/i.test(name) || id === '200' || id === '300'
    }
    const ids = [...new Set([...Object.keys(agg), ...Object.keys(lastStats)])].filter((id) => !isBot(id))
    if (ids.length === 0) return {}

    const resolveName = (id) => displayNameFor(id)
    const result = {}
    result['talk-equity'] = {
      metricLabel: 'Talk time (minutes)',
      unit: 'min',
      rows: ids.map((id) => ({
        name: resolveName(id),
        lastMeeting: Math.round((lastStats[id]?.talkTime ?? 0) * 10) / 10,
        periodAvg: agg[id] ? Math.round((agg[id].totalTalkTimeMinutes / agg[id].meetingCount) * 10) / 10 : 0,
      })).sort((a, b) => b.periodAvg - a.periodAvg),
    }
    result['interruptions'] = {
      metricLabel: 'Interruptions caused (lower is better)',
      unit: 'count',
      rows: ids.map((id) => ({
        name: resolveName(id),
        lastMeeting: lastStats[id]?.interruptions ?? 0,
        periodAvg: agg[id] ? Math.round((agg[id].totalInterruptions / agg[id].meetingCount) * 10) / 10 : 0,
      })).sort((a, b) => b.periodAvg - a.periodAvg),
    }
    return result
  }, [periodDashboard?.aggregatedParticipants, lastMeeting])

  const policyModels = scorecardModels.map((model) => ({
    ...model,
    score:
      scores != null &&
      scores[model.id] != null &&
      typeof scores[model.id] === 'number' &&
      Number.isFinite(scores[model.id])
        ? scores[model.id]
        : null,
  }))

  const timeDisciplineDetail = useMemo(() => {
    const m = lastMeeting
    if (!m) return null
    const scheduledStart = m.scheduledStartTime ? new Date(m.scheduledStartTime) : null
    const scheduledEnd = m.scheduledEndTime ? new Date(m.scheduledEndTime) : null
    const actualStart = m.startedAtMs != null ? new Date(m.startedAtMs) : null
    const actualEnd = m.endedAtMs != null ? new Date(m.endedAtMs) : null
    const hasAny =
      scheduledStart != null ||
      scheduledEnd != null ||
      actualStart != null ||
      actualEnd != null ||
      m.earlyEndMinutes != null
    if (!hasAny) return null

    let timeLabel = null
    let diffMinutes = null
    if (
      scheduledStart != null &&
      scheduledEnd != null &&
      actualStart != null &&
      actualEnd != null
    ) {
      const scheduledDurationMin = (scheduledEnd - scheduledStart) / 60000
      const actualDurationMin = (actualEnd - actualStart) / 60000
      diffMinutes = Math.round((scheduledDurationMin - actualDurationMin) * 10) / 10
      if (diffMinutes > 0) timeLabel = `${diffMinutes} min under`
      else if (diffMinutes < 0) timeLabel = `${Math.abs(diffMinutes)} min over`
      else timeLabel = 'On time'
    } else if (m.earlyEndMinutes != null) {
      diffMinutes = m.earlyEndMinutes
      const earlyMin = m.earlyEndMinutes
      timeLabel =
        earlyMin > 0 ? `${earlyMin} min under` : earlyMin < 0 ? `${Math.abs(earlyMin)} min over` : 'On time'
    }

    return {
      scheduledStart,
      scheduledEnd,
      actualStart,
      actualEnd,
      timeUnderOver: timeLabel,
      diffMinutes,
    }
  }, [lastMeeting])

  const overallScore = (() => {
    if (!scores) return null
    const vals = scorecardModels
      .map((m) => scores[m.id])
      .filter(
        (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100
      )
    if (vals.length === 0) return null
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
  })()

  const formatDetailValue = (value, unit) => {
    if (unit === '%') return `${value}%`
    if (unit === 'min') return `${value}m`
    return value
  }

  const toggleModel = (modelId) => {
    setExpandedModels((prev) =>
      prev.includes(modelId) ? prev.filter((id) => id !== modelId) : [...prev, modelId]
    )
  }

  const chartSeries = useMemo(() => {
    const meetings = periodDashboard?.recentMeetingAnalytics ?? []
    const referenceNowMs = periodDashboard?.periodEnd
      ? new Date(periodDashboard.periodEnd).getTime()
      : null

    if (scoreTrendGranularity === 'per_meeting') {
      return buildPerMeetingTrendSeries({
        meetings,
        chartRange,
        referenceNowMs,
        scoreForMeeting: overallMeetingScore,
      })
    }

    return buildDailyOverallScoreTrendSeries({
      meetings,
      chartRange,
      referenceNowMs,
    })
  }, [periodDashboard, chartRange, scoreTrendGranularity])

  const hasData =
    scores &&
    Object.entries(scores).some(
      ([k, v]) =>
        k !== 'overallScore' &&
        k !== 'omittedDimensions' &&
        typeof v === 'number' &&
        v > 0
    )
  const periodBadge = analyticsLabel?.trim() || 'Selected period'

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-primary-50 flex items-center justify-center text-primary-600">
            <ClipboardCheck className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Meeting Scorecard</h2>
            <p className="text-gray-500 text-sm">
              {subtitle ??
                'Policy scores for the same period as Quick Analytics (top of page).'}
            </p>
          </div>
        </div>
      </motion.div>

      <div className="grid gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass p-6"
        >
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-1">
                <h2 className="text-xl font-semibold text-gray-900">Scorecard</h2>
                <span className="badge-primary">{periodBadge}</span>
              </div>
              <p className="text-sm text-gray-500">
                Each model is scored from 0–100 for the analytics period above. Click a model to see
                details. Participant columns compare your selected period to your most recent qualifying
                meeting.
              </p>
            </div>
          </div>

          {!hasData && (
            <div className="text-center py-12 text-gray-400">
              <p className="text-lg font-medium">No scorecard data for this period</p>
              <p className="text-sm mt-1">Try a wider time range or complete more meetings.</p>
            </div>
          )}

          {hasData && (
            <>
              <div className="mb-6 rounded-2xl border border-gray-100 bg-white/70 p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Overall Score</p>
                    <p className="text-4xl font-bold text-gray-900">
                      {overallScore != null ? overallScore : '—'}
                    </p>
                  </div>
                  <div className="w-full md:w-56">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${overallScore != null ? overallScore : 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-2 text-right">/100</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {policyModels.map((model, index) => {
                  const detail = participantBreakdowns[model.id]
                  const isExpanded = expandedModels.includes(model.id)

                  return (
                    <motion.div
                      key={model.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + index * 0.05 }}
                      className="rounded-2xl border border-gray-100 bg-white/70 p-4"
                    >
                      <button
                        type="button"
                        onClick={() => toggleModel(model.id)}
                        className="w-full text-left"
                        aria-expanded={isExpanded}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <p className="font-medium text-gray-900">{decodeHtmlEntities(model.name)}</p>
                            <p className="text-sm text-gray-500">{decodeHtmlEntities(model.description)}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-lg font-semibold text-gray-900">
                                {model.score != null ? model.score : 'Not available'}
                              </p>
                              {model.score != null ? (
                                <p className="text-xs text-gray-400">/100</p>
                              ) : null}
                            </div>
                            <ChevronDown
                              className={`w-4 h-4 text-gray-400 transition-transform ${
                                isExpanded ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                        </div>
                      </button>
                      <div className="progress-bar mt-3">
                        <div
                          className="progress-fill"
                          style={{ width: `${model.score != null ? model.score : 0}%` }}
                        />
                      </div>

                      {isExpanded && (
                        <div className="mt-4 border-t border-gray-200 pt-4">
                          {model.id === 'decision-velocity' && outputVelocityBreakdown ? (
                            <>
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
                                <p className="text-sm font-semibold text-gray-900">Output counts</p>
                                <span className="text-xs text-gray-400">{outputVelocityBreakdown.label}</span>
                              </div>
                              <div className="flex flex-wrap gap-4">
                                <div>
                                  <p className="text-2xl font-semibold text-gray-900">
                                    {outputVelocityBreakdown.decisionCount}
                                  </p>
                                  <p className="text-xs text-gray-400">decisions</p>
                                </div>
                                <div>
                                  <p className="text-2xl font-semibold text-gray-900">
                                    {outputVelocityBreakdown.actionItemCount}
                                  </p>
                                  <p className="text-xs text-gray-400">action items</p>
                                </div>
                                <div>
                                  <p className="text-2xl font-semibold text-gray-900">
                                    {outputVelocityBreakdown.followUpCount}
                                  </p>
                                  <p className="text-xs text-gray-400">follow ups</p>
                                </div>
                              </div>
                            </>
                          ) : model.id === 'decision-velocity' && !outputVelocityBreakdown ? (
                            <p className="text-sm text-gray-400">No output data available for this period.</p>
                          ) : model.id === 'agenda-adherence' && agendaAdherenceBreakdown ? (
                            <>
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
                                <p className="text-sm font-semibold text-gray-900">Agenda items</p>
                                <span className="text-xs text-gray-400">{agendaAdherenceBreakdown.label}</span>
                              </div>
                              <div className="flex flex-wrap gap-4">
                                <div>
                                  <p className="text-2xl font-semibold text-gray-900">
                                    {agendaAdherenceBreakdown.total}
                                  </p>
                                  <p className="text-xs text-gray-400">total</p>
                                </div>
                                <div>
                                  <p className="text-2xl font-semibold text-gray-900">
                                    {agendaAdherenceBreakdown.completed}
                                  </p>
                                  <p className="text-xs text-gray-400">completed</p>
                                </div>
                                <div>
                                  <p className="text-2xl font-semibold text-gray-900">
                                    {agendaAdherenceBreakdown.outstanding}
                                  </p>
                                  <p className="text-xs text-gray-400">outstanding / never started</p>
                                </div>
                                <div>
                                  <p className="text-2xl font-semibold text-gray-900">
                                    {agendaAdherenceBreakdown.tangentsDetected}
                                  </p>
                                  <p className="text-xs text-gray-400">tangents detected</p>
                                </div>
                              </div>
                            </>
                          ) : model.id === 'time-discipline' && timeDisciplineDetail ? (
                            <>
                              <p className="text-sm font-semibold text-gray-900 mb-3">
                                Scheduled vs actual (most recent meeting)
                              </p>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left border-none" style={{ borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr>
                                      <th className="text-gray-500 font-medium pb-2 pr-6 w-0" scope="col" />
                                      <th className="text-gray-500 font-medium pb-2 pr-6" scope="col">
                                        Scheduled
                                      </th>
                                      <th className="text-gray-500 font-medium pb-2 pr-6" scope="col">
                                        Actual
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td className="text-gray-500 py-1.5 pr-6 align-top">Start</td>
                                      <td className="text-gray-700 py-1.5 pr-6 align-top">
                                        {timeDisciplineDetail.scheduledStart
                                          ? format(timeDisciplineDetail.scheduledStart, 'MMM d, yyyy h:mm a')
                                          : '—'}
                                      </td>
                                      <td className="text-gray-900 font-medium py-1.5 pr-6 align-top">
                                        {timeDisciplineDetail.actualStart
                                          ? format(timeDisciplineDetail.actualStart, 'MMM d, yyyy h:mm a')
                                          : '—'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="text-gray-500 py-1.5 pr-6 align-top">End</td>
                                      <td className="text-gray-700 py-1.5 pr-6 align-top">
                                        {timeDisciplineDetail.scheduledEnd
                                          ? format(timeDisciplineDetail.scheduledEnd, 'MMM d, yyyy h:mm a')
                                          : '—'}
                                      </td>
                                      <td className="text-gray-900 font-medium py-1.5 pr-6 align-top">
                                        {timeDisciplineDetail.actualEnd
                                          ? format(timeDisciplineDetail.actualEnd, 'MMM d, yyyy h:mm a')
                                          : '—'}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              {timeDisciplineDetail.timeUnderOver != null && (
                                <div className="flex items-baseline gap-x-2 mt-3 pt-3 border-t border-gray-100">
                                  <span className="text-gray-500 text-sm shrink-0">Total time:</span>
                                  <span
                                    className={
                                      (timeDisciplineDetail.diffMinutes ?? 0) < 0
                                        ? 'text-amber-600 font-medium text-sm'
                                        : (timeDisciplineDetail.diffMinutes ?? 0) > 0
                                          ? 'text-emerald-600 font-medium text-sm'
                                          : 'text-gray-900 font-medium text-sm'
                                    }
                                  >
                                    {timeDisciplineDetail.timeUnderOver}
                                  </span>
                                </div>
                              )}
                            </>
                          ) : detail && detail.rows.length > 0 ? (
                            <>
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">Participant breakdown</p>
                                  <p className="text-xs text-gray-400">{detail.metricLabel}</p>
                                </div>
                                <span className="text-xs text-gray-400">
                                  Most recent meeting vs {detailPeriodLabel}
                                </span>
                              </div>
                              <div className="space-y-3">
                                {detail.rows.map((row) => (
                                  <div key={row.name} className="flex items-center justify-between gap-4">
                                    <div>
                                      <p className="font-medium text-gray-900">{decodeHtmlEntities(row.name)}</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                      <div className="text-right">
                                        <p className="text-sm font-medium text-gray-900">
                                          {formatDetailValue(row.lastMeeting, detail.unit)}
                                        </p>
                                        <p className="text-xs text-gray-400">most recent</p>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-sm font-medium text-gray-900">
                                          {formatDetailValue(row.periodAvg, detail.unit)}
                                        </p>
                                        <p className="text-xs text-gray-400">{detailPeriodLabel}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : model.id === 'time-discipline' ? (
                            <p className="text-sm text-gray-400">
                              No schedule data for the most recent meeting.
                            </p>
                          ) : model.id === 'agenda-adherence' ? (
                            <p className="text-sm text-gray-400">
                              Agenda item breakdown will appear here after meetings with agendas are completed.
                            </p>
                          ) : (
                            <p className="text-sm text-gray-400">
                              No participant breakdown available for this metric.
                            </p>
                          )}
                        </div>
                      )}
                    </motion.div>
                  )
                })}
              </div>
            </>
          )}
        </motion.div>

        <TrendChartCard
          icon={TrendingUp}
          title="Scorecard Trend"
          description={
            trendChartDescription ??
            (scoreTrendGranularity === 'per_meeting'
              ? 'One point per qualifying occurrence (chronological).'
              : 'Daily trajectory (rolling window).')
          }
          series={chartSeries}
          loading={loading}
          ariaLabel="Scorecard trend chart"
          transitionDelay={0.2}
          showRangeSelector={false}
          onTrendMeetingNavigate={
            scoreTrendGranularity === 'per_meeting' ? onScoreTrendMeetingNavigate : undefined
          }
        />
      </div>
    </div>
  )
}
